/**
 * A message's files in the transcript (TMMAPP-0142 item 25b; TMMAPI-0181 U7):
 * - a picture is a thumbnail that opens a viewer (with Download);
 * - any other file is a chip that downloads it.
 * Every read is `GET …/attachments/{id}` through the bearer client (the transport has no cookie, so
 * never a plain link). A picture loads only while it's in view (K1, `useLazyObjectUrl`): its bytes
 * live in memory as an object URL while it's seen or open in the viewer, and are dropped when it
 * scrolls away; nothing is put in browser storage. In a shared session each file says who added it.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantAttachment } from '@/api/assistant';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { DownloadIcon } from '@/components/icons';
import { personName } from '@/i18n/personName';
import { useLazyObjectUrl } from '@/shared/useLazyObjectUrl';
import { resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { AttachmentFacts, AttachmentKindIcon } from './composer/AttachmentFacts';

const VIEWER_TITLE_ID = 'assistant-attachment-viewer-title';

function canPreview(): boolean {
  return typeof URL.createObjectURL === 'function';
}

/** Who added a file, in a shared session: "Added by Grace Hopper", or "Added by you". */
function Uploader({ attachment }: { attachment: AssistantAttachment }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Typography sx={{ fontSize: 12, color: tokens.text2 }} data-attachment-uploader="">
      {attachment.mine
        ? t('assistant.attach.addedByYou')
        : t('assistant.attach.addedBy', { name: personName(attachment.uploaderName, t) })}
    </Typography>
  );
}

function FileChip({
  sessionId,
  attachment,
}: {
  sessionId: number;
  attachment: AssistantAttachment;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const { blob, filename } = await assistantApi.downloadAttachment(
        sessionId,
        attachment.attachmentId,
      );
      downloadBlob(blob, filename ?? attachment.name);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack sx={{ gap: 0.25, minWidth: 0 }}>
      <ButtonBase
        onClick={() => void save()}
        aria-label={t('assistant.attach.download', { name: attachment.name })}
        aria-busy={busy}
        data-attachment-file={attachment.attachmentId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          p: 0.75,
          paddingInlineEnd: 1.25,
          maxWidth: { xs: '100%', sm: 280 },
          minWidth: 0,
          textAlign: 'start',
          border: `1px solid ${tokens.line}`,
          borderRadius: 1.5,
          bgcolor: tokens.surface,
          color: tokens.text,
          '&:hover': { bgcolor: tokens.hover },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: tokens.surface2,
            color: tokens.text,
          }}
        >
          <AttachmentKindIcon kind={attachment.kind} />
        </Box>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {attachment.name}
          </Typography>
          <AttachmentFacts
            sizeBytes={attachment.sizeBytes}
            pages={attachment.pages}
            rows={attachment.rows}
            truncated={attachment.truncated}
          />
        </Stack>
        <Box sx={{ color: tokens.text2, display: 'flex' }}>
          <DownloadIcon />
        </Box>
      </ButtonBase>
      {failed && (
        <Typography sx={{ fontSize: 12, color: tokens.errorText }} data-attachment-failed="">
          {t('assistant.attach.downloadFailed', { name: attachment.name })}
        </Typography>
      )}
    </Stack>
  );
}

function ImageViewer({
  attachment,
  url,
  blob,
  onClose,
}: {
  attachment: AssistantAttachment;
  url: string;
  blob: Blob;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={VIEWER_TITLE_ID}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle id={VIEWER_TITLE_ID} sx={{ overflowWrap: 'anywhere' }}>
        {attachment.name}
      </DialogTitle>
      <DialogContent>
        <Box
          component="img"
          src={url}
          alt={attachment.name}
          sx={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', mx: 'auto' }}
        />
        <Box sx={{ mt: 1.5 }}>
          <Button startIcon={<DownloadIcon />} onClick={() => downloadBlob(blob, attachment.name)}>
            {t('assistant.attach.downloadButton')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
}

function ImageThumb({
  sessionId,
  attachment,
}: {
  sessionId: number;
  attachment: AssistantAttachment;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [open, setOpen] = useState(false);
  // Loaded while in view, and kept while the viewer shows it (K1).
  const { observe, url, blob, failed } = useLazyObjectUrl(
    async (signal) =>
      (await assistantApi.downloadAttachment(sessionId, attachment.attachmentId, signal)).blob,
    { keep: open },
  );
  const image = url !== null && blob !== null ? { url, blob } : null;

  // A picture that can't be shown is still its file.
  if (failed) return <FileChip sessionId={sessionId} attachment={attachment} />;
  return (
    <>
      <ButtonBase
        ref={observe}
        onClick={() => setOpen(true)}
        disabled={image === null}
        aria-label={t('assistant.attach.open', { name: attachment.name })}
        data-attachment-image={attachment.attachmentId}
        sx={{
          width: 96,
          height: 96,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: `1px solid ${tokens.line}`,
          bgcolor: tokens.surface2,
          color: tokens.text,
        }}
      >
        {image === null ? (
          <AttachmentKindIcon kind="IMAGE" />
        ) : (
          <Box
            component="img"
            src={image.url}
            alt={attachment.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </ButtonBase>
      {open && image !== null && (
        <ImageViewer
          attachment={attachment}
          url={image.url}
          blob={image.blob}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function MessageAttachments({
  sessionId,
  attachments,
  shared,
}: {
  sessionId: number;
  attachments: readonly AssistantAttachment[];
  shared: boolean;
}) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;
  const preview = canPreview();
  return (
    <Stack
      direction="row"
      role="list"
      aria-label={t('assistant.attach.messageFiles')}
      sx={{ gap: 1, flexWrap: 'wrap', mt: 1 }}
      data-message-attachments=""
    >
      {attachments.map((attachment) => (
        <Stack role="listitem" key={attachment.attachmentId} sx={{ gap: 0.25, minWidth: 0 }}>
          {attachment.kind === 'IMAGE' && preview ? (
            <ImageThumb sessionId={sessionId} attachment={attachment} />
          ) : (
            <FileChip sessionId={sessionId} attachment={attachment} />
          )}
          {shared && <Uploader attachment={attachment} />}
        </Stack>
      ))}
    </Stack>
  );
}
